import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { ICountry, IShipco, IShipcoGroup } from "interfaces";
import React from "react";

export const ShipcoGroupList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IShipcoGroup>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IShipcoGroup>(
    {
      mapData: (item) => {
        return {
          "Registration Name": item?.registrationName,
          "Registration Number": item?.registrationNumber || "",
          "GST Number": item?.gstNumber || "",
          "Accreditation Number": item?.accreditationNumber || "",
          "Email": item?.email || "",
          "Phone": item?.phone || "",
          "Country": item?.address?.country?.name || "",
          "Address": item?.address
            ? `${item?.address?.city || ""} ${
                item?.address?.city && item?.address?.state ? "," : ""
              } ${item?.address?.state || ""}`
            : "",
          "Shipco": item?.shipco?.registrationName || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "Registration Name",
          "Registration Number",
          "GST Number",
          "Accreditation Number",
          "Email",
          "Phone",
          "Country",
          "Address",
          "Shipco",
        ],
      },
    }
  );

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: regNameSelectProps } = useSelect<IShipcoGroup>({
    resource: "shipco-group",
    optionLabel: "registrationName",
    optionValue: "registrationName",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: countrySelectProps } = useSelect<ICountry>({
    resource: "country",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IShipcoGroup> = [
    {
      title: "Reg Name",
      dataIndex: "registrationName",
      key: "registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Reg Name"
            {...regNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Country",
      dataIndex: ["address", "country", "name"],
      key: "address.country.name",
      width: 150,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Country"
            {...countrySelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Communication Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      className: "wrap-text",
      render: (value) => <CopyableEmailTag value={value} />,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      width: 200,
      className: "wrap-text",
      render: (address) => (
        <React.Fragment>
          {address?.city ? (
            <React.Fragment>
              <TextField value={address.city} />
              ,&nbsp;
            </React.Fragment>
          ) : (
            ""
          )}
          {address?.state ? (
            <React.Fragment>
              <TextField value={address.state} />
            </React.Fragment>
          ) : (
            ""
          )}
        </React.Fragment>
      ),
    },
    {
      title: "Shipco",
      dataIndex: ["shipco", "registrationName"],
      key: "shipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "Shipco Groups",
        subTitle: "active shipping company groups",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
